import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLink, MessageSquare, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { RowActionItem } from '@/components/ui/row-actions';
import { STEPS } from '@/lib/outreach-copy';
import { useWithdrawContact } from '@/hooks/useContacts';
import {
  enrichmentField,
  messagedAgo,
  queueActionError,
  useUnmarkContacted,
} from '@/hooks/useDiscoveryQueue';
import type { ContactedNotReplied, DiscoveryProspect } from '@/api/types';
import { LeadRow } from './LeadRow';
import { compactNumber, profileUrl, prospectProfileUrl } from './profile';

/**
 * A creator the caller messaged who hasn't replied, on My creators' Messaged
 * tab. Same row and same primary action (Log reply) as the Lead queue's
 * "Messaged · waiting" tab, so a self-found creator and a queue lead read and
 * act identically at this stage.
 *
 * When the creator is also a queue lead (`prospect`), Log reply goes through
 * the queue's dialog so the lead is marked logged; otherwise it opens the
 * Log reply page with the handle filled in.
 */
export function MessagedCreatorRow({
  row,
  prospect,
  onLogReply,
}: {
  row: ContactedNotReplied;
  prospect?: DiscoveryProspect;
  onLogReply: (p: DiscoveryProspect) => void;
}): JSX.Element {
  const unmarkLead = useUnmarkContacted();
  const withdraw = useWithdrawContact();
  const pending = unmarkLead.isPending || withdraw.isPending;
  const href = prospect ? prospectProfileUrl(prospect) : profileUrl(row.channel, row.targetIdentifier);

  const undo = (): void => {
    const opts = {
      onSuccess: () => toast.success('Removed your messaged mark'),
      onError: (e: unknown) => toast.error(queueActionError(e)),
    };
    // A queue lead goes through the lead's own route, which also clears the
    // lead's Messaged flag when the last mark goes.
    if (prospect) unmarkLead.mutate(prospect.publicId, opts);
    else withdraw.mutate({ channel: row.channel, targetIdentifier: row.targetIdentifier }, opts);
  };

  const actions: Array<RowActionItem | null> = [
    { label: `${STEPS.messaged.undo} messaged`, icon: Undo2, disabled: pending, onClick: undo },
    href
      ? {
          label: 'Open profile',
          icon: ExternalLink,
          onClick: () => window.open(href, '_blank', 'noopener,noreferrer'),
        }
      : null,
  ];

  const logParams = new URLSearchParams({ channel: row.channel, handle: row.targetIdentifier });

  return (
    <LeadRow
      channel={row.channel}
      handle={row.targetIdentifier}
      href={href}
      followers={prospect ? compactNumber(enrichmentField(prospect, 'followers')) : null}
      avgViews={prospect ? compactNumber(enrichmentField(prospect, 'avgViews')) : null}
      meta={[
        prospect ? `Lead queue: ${prospect.sourceFolderName ?? 'Unfiled'}` : 'Found myself',
        messagedAgo(row.lastContactedAt),
        row.attemptCount > 1 && `${row.attemptCount} messages`,
      ]}
      step="messaged"
      primary={
        prospect ? (
          <Button size="sm" onClick={() => onLogReply(prospect)} title={STEPS.logged.means}>
            <MessageSquare className="mr-1 h-3.5 w-3.5" />
            {STEPS.logged.verb}
          </Button>
        ) : (
          <Button size="sm" asChild title={STEPS.logged.means}>
            <Link to={`/portal/log?${logParams.toString()}`}>
              <MessageSquare className="mr-1 h-3.5 w-3.5" />
              {STEPS.logged.verb}
            </Link>
          </Button>
        )
      }
      actions={actions}
    />
  );
}
